import { IUser, getUserShareTitle } from '@/interfaces/user'
import { ProfileRelationType } from '../types/profile'
import { validateConnectionExistsById } from '@/components/requestConnectionButton/validateConnectionSentReceived'
import { User } from 'containers/user/types/user'
import { ProfileRecommendation } from 'containers/recommendations/types/recommendations'
import { ProfileRecommendationType } from '@/interfaces/recommendation'
import { APP_LINKS, joinWithSlash } from 'utilities/links'
import { userFullName } from '@/shared/utils'

export interface ProfileMetaData {
  title?: string
  description?: string
  image?: string
  twitterImage?: string
}

export const getProfileMetaData = (
  profile: IUser | User,
  optionalData?: ProfileMetaData
) => {
  const defaultDescription = `Sign up now to explore ${userFullName(
    profile
  )}'s creator portfolio on Creatorland — where the creator economy thrives. Unlock full access to ${
    profile?.firstName + "'s" || 'their'
  } projects, insights and professional network, while connecting with industry leaders and harnessing opportunities designed to elevate creators like you.`

  return {
    title: optionalData?.title || getUserShareTitle(profile),
    description: optionalData?.description || defaultDescription,
    image: optionalData?.image || profile?.avatar,
    twitterImage: optionalData?.twitterImage || profile?.avatar,
    canonicalPath: `/${profile?.username?.toLocaleLowerCase()}`,
  }
}

export const validateConnectionType = ({
  user,
  profile,
}: {
  user?: IUser | User
  profile?: IUser | User
}) => {
  if (!user) return null
  if (user?.id === profile?.id) return ProfileRelationType.OWN_PROFILE
  if (user?.connections?.includes(profile?.id))
    return ProfileRelationType.CONNECTION
  if (
    validateConnectionExistsById({
      id: profile?.id,
      connections: user?.connectionsReceived,
    })
  )
    return ProfileRelationType.CONNECTION_RECEIVED
  if (
    validateConnectionExistsById({
      id: profile?.id,
      connections: user?.connectionsSent,
    })
  )
    return ProfileRelationType.CONNECTION_REQUESTED
  if (user?.blockedUsers?.includes(profile?.id))
    return ProfileRelationType.BLOCKED

  return ProfileRelationType.NONE
}

export const getProfileSubtitleCardCopy = (
  profile: Partial<IUser | User | ProfileRecommendation>
) => {
  let res = ''
  let wasCompleted = false

  const modifyRes = (value: string) => {
    if (wasCompleted || !value) return
    if (res.length > 0) {
      res += ' | ' + value
    } else {
      res += value
    }
    wasCompleted = true
  }
  if (profile?.location) {
    res = profile?.location
  }
  if (profile?.creatorType?.length) {
    modifyRes(profile?.creatorType?.slice(0, 2)?.join(', '))
  }
  if (profile?.contentType?.length) {
    modifyRes(profile?.contentType?.slice(0, 2)?.join(', '))
  }
  if (profile?.isEmployer?.companyName) {
    modifyRes(profile?.isEmployer?.companyName)
  }

  if (profile?.title) {
    modifyRes(profile?.title)
  }

  if ((profile as ProfileRecommendation)?.company) {
    modifyRes((profile as ProfileRecommendation)?.company)
  }

  return res
}

export const getProfileLink = (
  profile: Partial<IUser | User | ProfileRecommendation>
) => {
  const profileLink =
    (profile as ProfileRecommendation)?.recommendationType ===
    ProfileRecommendationType.COLD_PROFILE
      ? joinWithSlash(APP_LINKS.PROFILE_COLD_PROFILE, profile?.id)
      : `/${profile?.username?.toLocaleLowerCase()}`
  return profileLink
}
